import React, { useState, useEffect, useCallback } from "react";
import myAxios from "../AxiosUrl";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { getAdminPermissions } from "../../utils/adminPermissions";
import { useSearchParams } from "react-router-dom";
import UserOverviewSection from "../Dashboard/UserOverviewSection";
import { getUserAccessStatus } from "../../utils/userAccessStatus";

const USER_SEGMENTS = [
  "all",
  "registered",
  "admin",
  "verified",
  "pending",
  "subscription_access",
  "bypass",
  "no_access",
  "trial",
  "paid",
  "none",
];
const USER_SEGMENT_LABELS = {
  all: "All accounts",
  registered: "Registered users",
  admin: "Admin accounts",
  verified: "Verified",
  pending: "Pending verification",
  subscription_access: "Subscription access",
  bypass: "Bypass access",
  no_access: "No access",
  trial: "TopSekk free month",
  paid: "Paid",
  none: "No active subscription",
};

const USER_TABLE_COLUMNS =
  "minmax(220px,1fr) 120px 95px 150px 170px 120px 110px 110px 130px";
const USER_TABLE_MIN_WIDTH = "1320px";

const PLATFORM_LABELS = {
  app_store: "Apple App Store",
  google_play: "Google Play",
  stripe: "Stripe",
};

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(value))
    : "—";

// Custom Icons as SVG components
const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

const EditIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

const ViewIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const LeftArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
  </svg>
);

const RightArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
  </svg>
);

/**
 * Get full CDN image URL for user profile images
 *
 * CDN auto-generates unique filenames: {originalName}_{timestamp}_{randomId}.{ext}
 * Example: https://cdn.topsekk.com/file/user_profile_1762546646769_xyz123.png
 *
 * ALL images must be stored as full CDN URLs in the database
 * No relative paths or legacy formats are supported
 *
 * @param {string} url - Image URL (must be full CDN URL)
 * @returns {string|null} Full CDN URL or null
 */
const getFullImageUrl = (url) => {
  if (!url) return null;

  // All images should be full CDN URLs (https://cdn.topsekk.com/file/...)
  // If URL already starts with http:// or https://, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Legacy fallback: if somehow a relative path exists, log warning and return null
  // This should not happen in production after migration to CDN-only format
  console.warn(`[LEGACY] Non-CDN image URL detected for user: "${url}". Please update database to use CDN format (auto-generated by CDN)`);
  return null;
};

const normalizeRole = (value = "") => String(value).trim().toLowerCase();

const isSuperAdminAccount = (user) => {
  const role = normalizeRole(user?.role);
  return Boolean(
    user?.isSuperAdmin || role === "superadmin" || role === "super_admin"
  );
};

const isAdminAccount = (user) => {
  const role = normalizeRole(user?.role);
  return isSuperAdminAccount(user) || role === "admin";
};

const getRoleDisplayName = (user) => {
  if (isSuperAdminAccount(user)) return "Super Admin";
  if (isAdminAccount(user)) return "Admin";
  return "User";
};

const getRoleBadgeBackground = (user) => {
  if (isSuperAdminAccount(user)) {
    return "linear-gradient(135deg, #f59e0b, #d97706)";
  }
  if (isAdminAccount(user)) {
    return "linear-gradient(135deg, #667eea, #764ba2)";
  }
  return "linear-gradient(135deg, #8b5cf6, #7c3aed)";
};

// Avatar component for users without profile images
const Avatar = ({ user, size = 40 }) => {
  const [imageError, setImageError] = useState(false);

  // Reset image error when user changes
  useEffect(() => {
    setImageError(false);
  }, [user.profileImageUrl]);

  const getInitials = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(
        0
      )}`.toUpperCase();
    } else if (user.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    } else if (user.username) {
      return user.username.charAt(0).toUpperCase();
    } else if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  const getDisplayName = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.firstName) {
      return user.firstName;
    } else if (user.username) {
      return user.username;
    } else {
      return user.email;
    }
  };

  const avatarStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: `${size * 0.4}px`,
    fontWeight: "600",
    color: "#ffffff",
    background: getRoleBadgeBackground(user),
    border: "2px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  };

  // Use profileImageUrl instead of profileImage, and handle image errors
  if (user.profileImageUrl && !imageError) {
    return (
      <img
        src={getFullImageUrl(user.profileImageUrl)}
        alt={getDisplayName(user)}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: "50%",
          objectFit: "cover",
          border: "2px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
        onError={() => setImageError(true)}
      />
    );
  }

  return <div style={avatarStyle}>{getInitials(user)}</div>;
};

function UserManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSegment = searchParams.get("segment");
  const initialSegment = USER_SEGMENTS.includes(requestedSegment)
    ? requestedSegment
    : "registered";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToView, setUserToView] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const usersPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [segment, setSegment] = useState(initialSegment);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const { isAdmin, isSuperAdmin } = getAdminPermissions();
  const currentAdminId = localStorage.getItem("id");

  const canManageUser = useCallback(
    (targetUser) => {
      if (!isAdmin) return false;
      if (isSuperAdmin) return true;
      return !isAdminAccount(targetUser);
    },
    [isAdmin, isSuperAdmin]
  );

  const canEditUser = useCallback(
    (targetUser) => canManageUser(targetUser),
    [canManageUser]
  );

  const canDeleteUser = useCallback(
    (targetUser) => canManageUser(targetUser),
    [canManageUser]
  );

  const fetchUsers = useCallback(async (targetPage) => {
    try {
      setLoading(true);
      const usesAccountEndpoint = segment === "all" || segment === "admin";
      const res = await myAxios.get(
        usesAccountEndpoint ? "/users" : "/dashboard/user-analytics",
        {
          params: {
            page: targetPage,
            limit: usersPerPage,
            search,
            ...(segment === "admin"
              ? { role: "admin" }
              : segment === "all"
                ? {}
                : { segment, period: "30d" }),
          },
        }
      );

      const analyticsUsers = res.data?.data?.users;
      const responseUsers = usesAccountEndpoint
        ? res.data?.data?.users || []
        : (analyticsUsers?.items || []).map((user) => ({
            ...user,
            _id: user.id,
          }));
      const pagination = usesAccountEndpoint
        ? res.data?.data?.pagination || {}
        : {
            totalPages: analyticsUsers?.pages,
            totalUsers: analyticsUsers?.total,
          };

      setUsers(responseUsers);
      setTotalPages(pagination.totalPages || 1);
      setTotalUsers(pagination.totalUsers || responseUsers.length);
      setError(null);
    } catch (err) {
      setError("Failed to fetch users");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, segment, usersPerPage]);

  useEffect(() => {
    fetchUsers(page);
  }, [page, fetchUsers]);

  useEffect(() => {
    if (USER_SEGMENTS.includes(requestedSegment) && requestedSegment !== segment) {
      setSegment(requestedSegment);
      setPage(1);
    }
  }, [requestedSegment, segment]);

  const changeSegment = (value) => {
    setSegment(value);
    setPage(1);
    setSearchInput("");
    setSearch("");
    setSearchParams(value === "all" ? {} : { segment: value });
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  useEffect(() => {
    // Check screen size and add resize listener
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Helper function to get user display name
  const getUserDisplayName = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.firstName) {
      return user.firstName;
    } else if (user.username) {
      return user.username;
    } else {
      return user.email;
    }
  };

  // View user handler
  const handleViewClick = (user) => {
    setUserToView(user);
    setViewDialogOpen(true);
  };

  // Edit user handlers
  const handleEditClick = (user) => {
    if (!canEditUser(user)) {
      toast.error("Admins cannot edit other admin accounts");
      return;
    }

    const safeRole = isAdminAccount(user) ? "admin" : "user";

    setUserToEdit(user);
    setEditFormData({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      username: user.username || "",
      role: safeRole,
      isActive: user.isActive || false,
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!userToEdit) return;
    if (!canEditUser(userToEdit)) {
      toast.error("You do not have permission to edit this account");
      return;
    }

    if (
      isSuperAdmin &&
      currentAdminId &&
      String(userToEdit._id) === String(currentAdminId) &&
      editFormData.role !== "admin"
    ) {
      toast.error("Super admin cannot remove own super-admin access");
      return;
    }

    if (!isSuperAdmin && editFormData.role === "admin") {
      toast.error("Only super admin can assign admin role");
      return;
    }

    try {
      const payload = {
        firstName: editFormData.firstName,
        lastName: editFormData.lastName,
        username: editFormData.username,
        role: editFormData.role,
        isActive: editFormData.isActive,
      };

      const res = await myAxios.put(`/users/${userToEdit._id}`, payload);
      toast.success("User updated successfully");

      // Update the users list
      setUsers((prev) =>
        prev.map((u) =>
          u._id === userToEdit._id ? { ...u, ...(res.data?.data || payload) } : u
        )
      );

      setEditDialogOpen(false);
      setUserToEdit(null);
      setEditFormData({});
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update user");
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setUserToEdit(null);
    setEditFormData({});
  };

  // Delete user handlers
  const handleDeleteClick = (user) => {
    if (!canDeleteUser(user)) {
      toast.error("Admins cannot delete admin accounts");
      return;
    }

    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    if (!canDeleteUser(userToDelete)) {
      toast.error("You do not have permission to delete this account");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      return;
    }

    try {
      setDeleteLoading(true);
      await myAxios.delete(`/users/${userToDelete._id}`);
      toast.success("User deleted successfully");

      const nextPage = users.length === 1 && page > 1 ? page - 1 : page;
      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        await fetchUsers(page);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete user");
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Pagination logic
  const paginatedUsers = users;

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  // Pagination button styles
  const basePaginationButtonStyle = {
    minWidth: "40px",
    height: "40px",
    border: "1px solid rgba(102, 126, 234, 0.2)",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    userSelect: "none",
    outline: "none",
    fontFamily: "inherit",
    background: "rgba(102, 126, 234, 0.1)",
    color: "#667eea",
  };

  const activePaginationButtonStyle = {
    ...basePaginationButtonStyle,
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    color: "#ffffff",
    border: "1px solid rgba(102, 126, 234, 0.3)",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
    transform: "translateY(-1px)",
  };

  const inactivePaginationButtonStyle = {
    ...basePaginationButtonStyle,
    background: "rgba(102, 126, 234, 0.08)",
    color: "#667eea",
    border: "1px solid rgba(102, 126, 234, 0.15)",
  };

  const disabledPaginationButtonStyle = {
    ...basePaginationButtonStyle,
    background: "rgba(0, 0, 0, 0.05)",
    color: "rgba(0, 0, 0, 0.3)",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    cursor: "not-allowed",
    opacity: "0.6",
  };

  // Mobile Card Component
  const MobileUserCard = ({ user, index }) => {
    const roleDisplayName = getRoleDisplayName(user);
    const roleBadgeBackground = getRoleBadgeBackground(user);
    const canEdit = canEditUser(user);
    const canDelete = canDeleteUser(user);
    const accessStatus = getUserAccessStatus(user);

    return (
      <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      style={{
        background: "rgba(255, 255, 255, 0.98)",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "16px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
        border: "1px solid rgba(0, 0, 0, 0.05)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          <Avatar user={user} size={48} />
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#6b7280",
                  background: "rgba(102, 126, 234, 0.1)",
                  padding: "4px 8px",
                  borderRadius: "6px",
                }}
              >
                #{(page - 1) * usersPerPage + index + 1}
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "unset",
                  height: "26px",
                  padding: "0 12px",
                  background: roleBadgeBackground,
                  color: "#ffffff",
                  borderRadius: "12px",
                  fontSize: "12px",
                  fontWeight: "600",
                  letterSpacing: "0.02em",
                  textTransform: "capitalize",
                  whiteSpace: "nowrap",
                }}
              >
                {roleDisplayName}
              </span>
            </div>

            <h3
              style={{
                margin: "0 0 4px 0",
                fontSize: "16px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              {getUserDisplayName(user)}
            </h3>

            {user.username && (
              <p
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "13px",
                  color: "#8b5cf6",
                  fontWeight: "500",
                }}
              >
                @{user.username}
              </p>
            )}

            <p
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                color: "#6b7280",
                wordBreak: "break-word",
              }}
            >
              {user.email}
            </p>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  fontWeight: "500",
                }}
              >
                Status:
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "60px",
                  height: "24px",
                  background: user.isActive
                    ? "linear-gradient(135deg, #10b981, #059669)"
                    : "linear-gradient(135deg, #ef4444, #dc2626)",
                  color: "#ffffff",
                  borderRadius: "12px",
                  fontSize: "11px",
                  fontWeight: "600",
                  letterSpacing: "0.025em",
                }}
              >
                {user.isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <div style={{ marginTop: "12px", display: "grid", gap: "6px", fontSize: "12px", color: "#6b7280" }}>
              <span>Email: <strong style={{ color: user.emailVerified ? "#15803d" : "#b56806" }}>{user.emailVerified ? "Verified" : "Pending"}</strong></span>
              <span>Subscription: <strong style={{ color: "#374151" }}>{user.subscription ? (user.subscription.status === "trialing" ? "Legacy store trial" : "Paid") : "No active subscription"}</strong></span>
              <span>Access: <strong style={{ color: accessStatus.color }}>{accessStatus.label}</strong></span>
              {user.subscription && <span>{PLATFORM_LABELS[user.subscription.platform] || "Unknown store"} · valid until {formatDate(user.subscription.trialEnd || user.subscription.currentPeriodEnd)}</span>}
              {user.freeAccessActive && <span>TopSekk free month · valid until {formatDate(user.freeAccessExpiresAt)}</span>}
              <span>Joined: {formatDate(user.createdAt)}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px",
          paddingTop: "16px",
          borderTop: "1px solid rgba(0, 0, 0, 0.05)",
        }}
      >
        <button
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            border: "1px solid rgba(102, 126, 234, 0.2)",
            background: "rgba(102, 126, 234, 0.1)",
            color: "#667eea",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            fontSize: "18px",
            outline: "none",
          }}
          title="View"
          onClick={() => handleViewClick(user)}
        >
          <ViewIcon />
        </button>
        <button
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            border: "1px solid rgba(139, 92, 246, 0.2)",
            background: "rgba(139, 92, 246, 0.1)",
            color: "#8b5cf6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: canEdit ? "pointer" : "not-allowed",
            transition: "all 0.2s ease",
            fontSize: "18px",
            outline: "none",
            opacity: canEdit ? 1 : 0.5,
          }}
          title={canEdit ? "Edit" : "You cannot edit this account"}
          onClick={() => handleEditClick(user)}
          disabled={!canEdit}
        >
          <EditIcon />
        </button>
        {isAdmin && (
          <button
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              border: "1px solid rgba(239, 68, 68, 0.2)",
              background: "rgba(239, 68, 68, 0.1)",
              color: "#ef4444",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: canDelete ? "pointer" : "not-allowed",
              transition: "all 0.2s ease",
              fontSize: "18px",
              outline: "none",
              opacity: canDelete ? 1 : 0.5,
            }}
            title={canDelete ? "Delete" : "You cannot delete this account"}
            onClick={() => handleDeleteClick(user)}
            disabled={!canDelete}
          >
            <DeleteIcon />
          </button>
        )}
      </div>
    </motion.div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          background: "#ffffff",
          minHeight: "100vh",
          padding: "40px 20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            borderRadius: "24px",
            padding: "80px",
            textAlign: "center",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            border: "1px solid #f1f3f4",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid #e5e7eb",
              borderTop: "4px solid #667eea",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 24px",
            }}
          />
          <p
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#374151",
              margin: 0,
            }}
          >
            Loading users...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          background: "#ffffff",
          minHeight: "100vh",
          padding: "40px 20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            borderRadius: "24px",
            padding: "80px",
            textAlign: "center",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            border: "1px solid #f1f3f4",
          }}
        >
          <div
            style={{
              fontSize: "64px",
              marginBottom: "24px",
              color: "#dc2626",
            }}
          >
            ❌
          </div>
          <p
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#374151",
              margin: 0,
            }}
          >
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        background: "#ffffff",
        minHeight: "100vh",
        padding: isMobile ? "20px 10px" : "40px 20px",
        overflow: "auto",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "24px",
          padding: isMobile ? "20px" : "32px",
          marginBottom: "32px",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
          border: "1px solid #f1f3f4",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            marginBottom: "16px",
            flexDirection: isMobile ? "column" : "row",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              borderRadius: "16px",
              padding: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <UserIcon />
          </div>
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: isMobile ? "24px" : "32px",
                fontWeight: "800",
                color: "#333",
                letterSpacing: "-0.02em",
              }}
            >
              User Management
            </h1>
            <p
              style={{
                margin: "4px 0 0 0",
                color: "#6b7280",
                fontSize: isMobile ? "14px" : "16px",
                fontWeight: "500",
              }}
            >
              Manage all system users and permissions
            </p>
          </div>
        </div>
      </div>

      <UserOverviewSection
        selectedSegment={segment === "all" ? null : segment}
        onSegmentChange={changeSegment}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          padding: "18px 20px",
          marginBottom: "16px",
          border: "1px solid #e7eaf0",
          borderRadius: "18px",
          background: "#ffffff",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {USER_SEGMENTS.map((value) => (
            <button
              type="button"
              key={value}
              aria-pressed={segment === value}
              onClick={() => changeSegment(value)}
              style={{
                padding: "9px 12px",
                border: segment === value ? "1px solid #667eea" : "1px solid #e1e4eb",
                borderRadius: "10px",
                background: segment === value ? "#667eea" : "#ffffff",
                color: segment === value ? "#ffffff" : "#62697a",
                fontSize: "12px",
                fontWeight: "700",
                cursor: "pointer",
              }}
            >
              {USER_SEGMENT_LABELS[value]}
            </button>
          ))}
        </div>
        <form onSubmit={submitSearch} style={{ display: "flex", gap: "8px" }}>
          <label htmlFor="user-management-search" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
            Search users
          </label>
          <input
            id="user-management-search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search name or email"
            style={{
              width: isMobile ? "190px" : "280px",
              padding: "10px 12px",
              border: "1px solid #dfe2e9",
              borderRadius: "10px",
              outline: "none",
            }}
          />
          <button
            type="submit"
            style={{
              padding: "10px 14px",
              border: 0,
              borderRadius: "10px",
              background: "#202433",
              color: "#ffffff",
              fontWeight: "700",
              cursor: "pointer",
            }}
          >
            Search
          </button>
        </form>
      </div>

      {/* Main Content */}
      <div>
        {isMobile ? (
          // Mobile Card Layout
          <div>
            {paginatedUsers.length > 0 ? (
              paginatedUsers.map((user, idx) => (
                <MobileUserCard key={user._id} user={user} index={idx} />
              ))
            ) : (
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: "24px",
                  padding: "60px 20px",
                  textAlign: "center",
                  color: "#6b7280",
                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #f1f3f4",
                }}
              >
                <div
                  style={{
                    fontSize: "48px",
                    marginBottom: "16px",
                    opacity: 0.5,
                  }}
                >
                  👥
                </div>
                <h3
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  No users found
                </h3>
                <p style={{ margin: 0, fontSize: "14px" }}>
                  There are no users to display at the moment.
                </p>
              </div>
            )}
          </div>
        ) : (
          // Desktop Table Layout
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              background: "#ffffff",
              borderRadius: "24px",
              marginBottom: "32px",
              overflowX: "auto",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              border: "1px solid #f1f3f4",
            }}
          >
            {/* Table Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: USER_TABLE_COLUMNS,
                minWidth: USER_TABLE_MIN_WIDTH,
                boxSizing: "border-box",
                gap: "8px",
                padding: "20px 32px",
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                borderBottom: "2px solid rgba(255, 255, 255, 0.1)",
                fontWeight: "600",
                fontSize: "12px",
                color: "white",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>User</div>
              <div style={{ display: "flex", alignItems: "center" }}>Email status</div>
              <div style={{ display: "flex", alignItems: "center" }}>Account</div>
              <div style={{ display: "flex", alignItems: "center" }}>Subscription</div>
              <div style={{ display: "flex", alignItems: "center" }}>Access</div>
              <div style={{ display: "flex", alignItems: "center" }}>Store</div>
              <div style={{ display: "flex", alignItems: "center" }}>Valid until</div>
              <div style={{ display: "flex", alignItems: "center" }}>Joined</div>
              <div style={{ display: "flex", alignItems: "center" }}>
                Actions
              </div>
            </div>

            {/* Table Body */}
            <div style={{ background: "#ffffff" }}>
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((user, idx) => {
                  const canEdit = canEditUser(user);
                  const canDelete = canDeleteUser(user);
                  const accessStatus = getUserAccessStatus(user);

                  return (
                    <motion.div
                    key={user._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    style={{
                      display: "grid",
                      gridTemplateColumns: USER_TABLE_COLUMNS,
                      minWidth: USER_TABLE_MIN_WIDTH,
                      boxSizing: "border-box",
                      gap: "8px",
                      padding: "20px 32px",
                      borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                      transition: "all 0.2s ease",
                      cursor: "default",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(102, 126, 234, 0.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "#ffffff";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <Avatar user={user} size={36} />
                      <div>
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: "600",
                            color: "#1f2937",
                            marginBottom: "2px",
                          }}
                        >
                          {getUserDisplayName(user)}
                        </div>
                        <div style={{ fontSize: "12px", color: "#6b7280", wordBreak: "break-word" }}>
                          {user.email}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span
                        style={{
                          padding: "5px 9px",
                          background: user.emailVerified ? "#e8f8ee" : "#fff3dc",
                          color: user.emailVerified ? "#15803d" : "#b56806",
                          borderRadius: "999px",
                          fontSize: "12px",
                          fontWeight: "700",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.emailVerified ? "Verified" : "Pending"}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "70px",
                          height: "28px",
                          background: user.isActive
                            ? "linear-gradient(135deg, #10b981, #059669)"
                            : "linear-gradient(135deg, #ef4444, #dc2626)",
                          color: "#ffffff",
                          borderRadius: "14px",
                          fontSize: "12px",
                          fontWeight: "600",
                          letterSpacing: "0.025em",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        {user.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", fontSize: "12px" }}>
                      {user.subscription ? (
                        <span style={{ padding: "5px 9px", borderRadius: "999px", background: user.subscription.status === "trialing" ? "#fff3dc" : "#e8f8ee", color: user.subscription.status === "trialing" ? "#b56806" : "#15803d", fontWeight: "700" }}>
                          {user.subscription.status === "trialing" ? "Legacy store trial" : "Paid"}
                        </span>
                      ) : (
                        <span style={{ color: "#9ca3af" }}>No active subscription</span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", fontSize: "12px" }}>
                      <span style={{ padding: "5px 9px", borderRadius: "999px", background: accessStatus.background, color: accessStatus.color, fontWeight: "700", whiteSpace: "nowrap" }}>
                        {accessStatus.label}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", color: "#6b7280", fontSize: "12px" }}>
                      {PLATFORM_LABELS[user.subscription?.platform] || "—"}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", color: "#6b7280", fontSize: "12px" }}>
                      {formatDate(user.subscription?.trialEnd || user.subscription?.currentPeriodEnd || user.freeAccessExpiresAt)}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", color: "#6b7280", fontSize: "12px" }}>
                      {formatDate(user.createdAt)}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <button
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "10px",
                          border: "1px solid rgba(102, 126, 234, 0.2)",
                          background: "rgba(102, 126, 234, 0.1)",
                          color: "#667eea",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          fontSize: "18px",
                          outline: "none",
                        }}
                        title="View"
                        onClick={() => handleViewClick(user)}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#667eea";
                          e.target.style.color = "#ffffff";
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow =
                            "0 4px 12px rgba(102, 126, 234, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background =
                            "rgba(102, 126, 234, 0.1)";
                          e.target.style.color = "#667eea";
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "none";
                        }}
                      >
                        <ViewIcon />
                      </button>
                      <button
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "10px",
                          border: "1px solid rgba(139, 92, 246, 0.2)",
                          background: "rgba(139, 92, 246, 0.1)",
                          color: "#8b5cf6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: canEdit ? "pointer" : "not-allowed",
                          transition: "all 0.2s ease",
                          fontSize: "18px",
                          outline: "none",
                          opacity: canEdit ? 1 : 0.5,
                        }}
                        title={canEdit ? "Edit" : "You cannot edit this account"}
                        onClick={() => handleEditClick(user)}
                        disabled={!canEdit}
                        onMouseEnter={(e) => {
                          if (!canEdit) return;
                          e.target.style.background = "#8b5cf6";
                          e.target.style.color = "#ffffff";
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow =
                            "0 4px 12px rgba(139, 92, 246, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                          if (!canEdit) return;
                          e.target.style.background = "rgba(139, 92, 246, 0.1)";
                          e.target.style.color = "#8b5cf6";
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "none";
                        }}
                      >
                        <EditIcon />
                      </button>
                      {isAdmin && (
                        <button
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "10px",
                            border: "1px solid rgba(239, 68, 68, 0.2)",
                            background: "rgba(239, 68, 68, 0.1)",
                            color: "#ef4444",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: canDelete ? "pointer" : "not-allowed",
                            transition: "all 0.2s ease",
                            fontSize: "18px",
                            outline: "none",
                            opacity: canDelete ? 1 : 0.5,
                          }}
                          title={
                            canDelete ? "Delete" : "You cannot delete this account"
                          }
                          onClick={() => handleDeleteClick(user)}
                          disabled={!canDelete}
                          onMouseEnter={(e) => {
                            if (!canDelete) return;
                            e.target.style.background = "#ef4444";
                            e.target.style.color = "#ffffff";
                            e.target.style.transform = "translateY(-2px)";
                            e.target.style.boxShadow =
                              "0 4px 12px rgba(239, 68, 68, 0.3)";
                          }}
                          onMouseLeave={(e) => {
                            if (!canDelete) return;
                            e.target.style.background = "rgba(239, 68, 68, 0.1)";
                            e.target.style.color = "#ef4444";
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "none";
                          }}
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </div>
                  </motion.div>
                  );
                })
              ) : (
                <div
                  style={{
                    padding: "60px 32px",
                    textAlign: "center",
                    color: "#6b7280",
                  }}
                >
                  <div
                    style={{
                      fontSize: "48px",
                      marginBottom: "16px",
                      opacity: 0.5,
                    }}
                  >
                    👥
                  </div>
                  <h3
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    No users found
                  </h3>
                  <p style={{ margin: 0, fontSize: "14px" }}>
                    There are no users to display at the moment.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Pagination */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: isMobile ? "4px" : "8px",
            padding: "24px",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1 || totalPages <= 1}
            style={{
              ...(page === 1 || totalPages <= 1
                ? disabledPaginationButtonStyle
                : inactivePaginationButtonStyle),
              minWidth: isMobile ? "36px" : "40px",
              height: isMobile ? "36px" : "40px",
              fontSize: isMobile ? "12px" : "14px",
            }}
            onMouseEnter={(e) => {
              if (page !== 1 && totalPages > 1) {
                e.target.style.background = "rgba(102, 126, 234, 0.15)";
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 2px 4px rgba(102, 126, 234, 0.2)";
              }
            }}
            onMouseLeave={(e) => {
              if (page !== 1 && totalPages > 1) {
                e.target.style.background = "rgba(102, 126, 234, 0.08)";
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }
            }}
          >
            <LeftArrowIcon />
          </button>

          {totalPages > 1 ? (
            Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => handlePageChange(i + 1)}
                style={{
                  ...(page === i + 1
                    ? activePaginationButtonStyle
                    : inactivePaginationButtonStyle),
                  minWidth: isMobile ? "36px" : "40px",
                  height: isMobile ? "36px" : "40px",
                  fontSize: isMobile ? "12px" : "14px",
                }}
                onMouseEnter={(e) => {
                  if (page !== i + 1) {
                    e.target.style.background = "rgba(102, 126, 234, 0.15)";
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow =
                      "0 2px 4px rgba(102, 126, 234, 0.2)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (page !== i + 1) {
                    e.target.style.background = "rgba(102, 126, 234, 0.08)";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }
                }}
              >
                {i + 1}
              </button>
            ))
          ) : (
            <button
              disabled={true}
              style={{
                ...disabledPaginationButtonStyle,
                minWidth: isMobile ? "36px" : "40px",
                height: isMobile ? "36px" : "40px",
                fontSize: isMobile ? "12px" : "14px",
              }}
            >
              1
            </button>
          )}

          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages || totalPages <= 1}
            style={{
              ...(page === totalPages || totalPages <= 1
                ? disabledPaginationButtonStyle
                : inactivePaginationButtonStyle),
              minWidth: isMobile ? "36px" : "40px",
              height: isMobile ? "36px" : "40px",
              fontSize: isMobile ? "12px" : "14px",
            }}
            onMouseEnter={(e) => {
              if (page !== totalPages && totalPages > 1) {
                e.target.style.background = "rgba(102, 126, 234, 0.15)";
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 2px 4px rgba(102, 126, 234, 0.2)";
              }
            }}
            onMouseLeave={(e) => {
              if (page !== totalPages && totalPages > 1) {
                e.target.style.background = "rgba(102, 126, 234, 0.08)";
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }
            }}
          >
            <RightArrowIcon />
          </button>

          <div
            style={{
              width: "100%",
              textAlign: "center",
              marginTop: "8px",
              fontSize: "13px",
              color: "#6b7280",
              fontWeight: "500",
            }}
          >
            {totalUsers} total {segment === "all" ? "accounts (including admins)" : segment === "admin" ? "admin accounts" : "users"}
          </div>
        </div>
      </div>

      {/* View User Modal */}
      <AnimatePresence>
        {viewDialogOpen && userToView && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              style={{
                background: "white",
                borderRadius: "24px",
                maxWidth: "600px",
                width: "100%",
                maxHeight: "80vh",
                overflow: "hidden",
                boxShadow: "0 24px 48px rgba(0, 0, 0, 0.2)",
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  padding: isMobile ? "20px" : "24px 32px",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <ViewIcon />
                  <h3
                    style={{
                      margin: 0,
                      fontSize: isMobile ? "18px" : "20px",
                      fontWeight: "700",
                    }}
                  >
                    User Details
                  </h3>
                </div>
                <button
                  onClick={() => setViewDialogOpen(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px",
                    cursor: "pointer",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CloseIcon />
                </button>
              </div>

              {/* Modal Content */}
              <div
                style={{
                  padding: isMobile ? "24px" : "32px",
                  background: "#f8fafc",
                  maxHeight: "60vh",
                  overflowY: "auto",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    marginBottom: "24px",
                  }}
                >
                  <Avatar user={userToView} size={64} />
                  <div>
                    <h4
                      style={{
                        margin: "0 0 4px 0",
                        fontSize: "24px",
                        fontWeight: "700",
                        color: "#1f2937",
                      }}
                    >
                      {getUserDisplayName(userToView)}
                    </h4>
                    {userToView.username && (
                      <p
                        style={{
                          margin: "0 0 8px 0",
                          fontSize: "16px",
                          color: "#8b5cf6",
                          fontWeight: "500",
                        }}
                      >
                        @{userToView.username}
                      </p>
                    )}
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "6px 14px",
                        background: getRoleBadgeBackground(userToView),
                        color: "#ffffff",
                        borderRadius: "16px",
                        fontSize: "14px",
                        fontWeight: "600",
                        textTransform: "capitalize",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getRoleDisplayName(userToView)}
                    </span>
                  </div>
                </div>

                <div style={{ display: "grid", gap: "16px" }}>
                  <div
                    style={{
                      background: "white",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <h5
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                      }}
                    >
                      Email
                    </h5>
                    <p
                      style={{ margin: 0, fontSize: "16px", color: "#1f2937" }}
                    >
                      {userToView.email}
                    </p>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        background: "white",
                        padding: "16px",
                        borderRadius: "12px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <h5
                        style={{
                          margin: "0 0 8px 0",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#6b7280",
                          textTransform: "uppercase",
                        }}
                      >
                        Status
                      </h5>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "6px 12px",
                          background: userToView.isActive
                            ? "linear-gradient(135deg, #10b981, #059669)"
                            : "linear-gradient(135deg, #ef4444, #dc2626)",
                          color: "#ffffff",
                          borderRadius: "12px",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        {userToView.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div
                      style={{
                        background: "white",
                        padding: "16px",
                        borderRadius: "12px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <h5
                        style={{
                          margin: "0 0 8px 0",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#6b7280",
                          textTransform: "uppercase",
                        }}
                      >
                        Member Since
                      </h5>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "16px",
                          color: "#1f2937",
                        }}
                      >
                        {new Date(userToView.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {userToView.lastActive && (
                    <div
                      style={{
                        background: "white",
                        padding: "16px",
                        borderRadius: "12px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <h5
                        style={{
                          margin: "0 0 8px 0",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#6b7280",
                          textTransform: "uppercase",
                        }}
                      >
                        Last Active
                      </h5>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "16px",
                          color: "#1f2937",
                        }}
                      >
                        {new Date(userToView.lastActive).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit User Modal */}
      <AnimatePresence>
        {editDialogOpen && userToEdit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              style={{
                background: "white",
                borderRadius: "24px",
                maxWidth: "600px",
                width: "100%",
                maxHeight: "80vh",
                overflow: "hidden",
                boxShadow: "0 24px 48px rgba(0, 0, 0, 0.2)",
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                  padding: isMobile ? "20px" : "24px 32px",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <EditIcon />
                  <h3
                    style={{
                      margin: 0,
                      fontSize: isMobile ? "18px" : "20px",
                      fontWeight: "700",
                    }}
                  >
                    Edit User
                  </h3>
                </div>
                <button
                  onClick={handleEditCancel}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px",
                    cursor: "pointer",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CloseIcon />
                </button>
              </div>

              {/* Modal Content */}
              <div
                style={{
                  padding: isMobile ? "24px" : "32px",
                  background: "#f8fafc",
                  maxHeight: "60vh",
                  overflowY: "auto",
                }}
              >
                <div style={{ display: "grid", gap: "20px" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        First Name
                      </label>
                      <input
                        type="text"
                        value={editFormData.firstName}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            firstName: e.target.value,
                          }))
                        }
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "2px solid #e5e7eb",
                          borderRadius: "12px",
                          fontSize: "16px",
                          outline: "none",
                          transition: "border-color 0.2s ease",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#8b5cf6")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={editFormData.lastName}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            lastName: e.target.value,
                          }))
                        }
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "2px solid #e5e7eb",
                          borderRadius: "12px",
                          fontSize: "16px",
                          outline: "none",
                          transition: "border-color 0.2s ease",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#8b5cf6")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      value={userToEdit.email || ""}
                      readOnly
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "12px",
                        fontSize: "16px",
                        background: "#f3f4f6",
                        color: "#6b7280",
                        cursor: "not-allowed",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      Username
                    </label>
                    <input
                      type="text"
                      value={editFormData.username}
                      onChange={(e) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          username: e.target.value,
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "12px",
                        fontSize: "16px",
                        outline: "none",
                        transition: "border-color 0.2s ease",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Role
                      </label>
                      <select
                        value={editFormData.role}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            role: e.target.value,
                          }))
                        }
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "2px solid #e5e7eb",
                          borderRadius: "12px",
                          fontSize: "16px",
                          outline: "none",
                          transition: "border-color 0.2s ease",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#8b5cf6")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                      >
                        <option value="user">User</option>
                        {isSuperAdmin && <option value="admin">Admin</option>}
                      </select>
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Status
                      </label>
                      <select
                        value={editFormData.isActive}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            isActive: e.target.value === "true",
                          }))
                        }
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "2px solid #e5e7eb",
                          borderRadius: "12px",
                          fontSize: "16px",
                          outline: "none",
                          transition: "border-color 0.2s ease",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#8b5cf6")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div
                style={{
                  padding: isMobile ? "20px" : "24px 32px",
                  background: "#f8fafc",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "16px",
                  flexDirection: isMobile ? "column-reverse" : "row",
                }}
              >
                <button
                  onClick={handleEditCancel}
                  style={{
                    background: "rgba(255, 255, 255, 0.9)",
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    padding: "12px 24px",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#6b7280",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.2s ease",
                    flex: isMobile ? "1" : "auto",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#ffffff";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(255, 255, 255, 0.9)";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                    border: "none",
                    borderRadius: "12px",
                    padding: "12px 24px",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "white",
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)",
                    transition: "all 0.2s ease",
                    flex: isMobile ? "1" : "auto",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 8px 20px rgba(139, 92, 246, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow =
                      "0 4px 12px rgba(139, 92, 246, 0.3)";
                  }}
                >
                  Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog
        open={deleteDialogOpen}
        onClose={deleteLoading ? undefined : handleDeleteCancel}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          {userToDelete ? (
            <p style={{ margin: 0, color: "#374151", lineHeight: 1.5 }}>
              Are you sure you want to delete{" "}
              <strong>{getUserDisplayName(userToDelete)}</strong>
              {userToDelete.email ? ` (${userToDelete.email})` : ""}?
            </p>
          ) : (
            <p style={{ margin: 0, color: "#374151" }}>
              Are you sure you want to delete this user?
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add CSS animation for loading spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default UserManagement;
